import { Skeleton } from "@cafe/ui";
import { lazy, Suspense, type ComponentType } from "react";
import { useTranslation } from "react-i18next";
import { createBrowserRouter, Navigate, Outlet } from "react-router";
import { GuestOnly, RequireAuth } from "@/features/auth/session";
import { useAuth } from "@/features/auth/store";
import { deviceEntry } from "@/features/terminal/device";
import { AdminShell } from "@/shells/admin-shell";
import { useCan, useMe, usePermissions } from "@/shared/hooks/session";
import { useBackGuard, useVisibleLaunch, useVisibleNav } from "@/shells/use-nav";
import { ComingSoon, PageGuard } from "@/shared/ui/guards";
import { NAV_ITEMS, type NavItem } from "./nav";

const route = (load: () => Promise<{ default: ComponentType }>) => async () => ({ Component: (await load()).default });

/** Реализованные экраны; остальные пункты навигации показывают «в разработке». */
const OnboardingPage = lazy(() => import("@/features/onboarding/onboarding-page"));
const PAGES: Record<string, ReturnType<typeof lazy>> = {
  "/dashboard": lazy(() => import("@/features/dashboard/dashboard-page")),
  "/help": lazy(() => import("@/features/help/help-page")),
  "/menu-packs": lazy(() => import("@/features/menu-packs/menu-packs-page")),
  "/warehouses": lazy(() => import("@/features/warehouse/warehouses-page")),
  "/transfers": lazy(() => import("@/features/warehouse/transfers-page")),
  "/expiry": lazy(() => import("@/features/warehouse/expiry-page")),
  "/stock-requests": lazy(() => import("@/features/warehouse/requests-page")),
  "/conversions": lazy(() => import("@/features/warehouse/conversions-page")),
  "/production": lazy(() => import("@/features/production/production-page")),
  "/branches": lazy(() => import("@/features/branches/branches-page")),
  "/staff/users": lazy(() => import("@/features/users/users-page")),
  "/staff/roles": lazy(() => import("@/features/roles/roles-page")),
  "/settings/company": lazy(() => import("@/features/settings/company-page")),
  "/settings/devices": lazy(() => import("@/features/devices/devices-page")),
  "/settings/subscription": lazy(() => import("@/features/settings/subscription-page")),
  "/menu": lazy(() => import("@/features/catalog/menu-page")),
  "/products": lazy(() => import("@/features/products/products-page")),
  "/costing": lazy(() => import("@/features/costing/costing-page")),
  "/tables": lazy(() => import("@/features/tables/tables-page")),
  "/waiter-zones": lazy(() => import("@/features/waiters/waiter-zones-page")),
  "/orders": lazy(() => import("@/features/orders/orders-page")),
  "/reports": lazy(() => import("@/features/reports/reports-page")),
  "/analytics": lazy(() => import("@/features/insights/insights-page")),
  "/expenses": lazy(() => import("@/features/finance/expenses-page")),
  "/purchases": lazy(() => import("@/features/finance/purchases-page")),
  "/purchase-requests": lazy(() => import("@/features/purchase-requests/purchase-requests-page")),
  "/supplier-returns": lazy(() => import("@/features/finance/supplier-returns-page")),
  "/writeoffs": lazy(() => import("@/features/finance/writeoffs-page")),
  "/inventory": lazy(() => import("@/features/inventory/stock-page")),
  "/inventory-counts": lazy(() => import("@/features/inventory/counts-page")),
  "/settings/notifications": lazy(() => import("@/features/notifications/settings-page")),
  "/settings/security": lazy(() => import("@/features/auth/security-page")),
  "/settings/report-reset": lazy(() => import("@/features/settings/report-reset-page")),
  "/staff/employees": lazy(() => import("@/features/staff/employees-page")),
  "/staff/positions": lazy(() => import("@/features/staff/positions-page")),
  "/staff/shifts": lazy(() => import("@/features/staff/shifts-page")),
  "/staff/payroll": lazy(() => import("@/features/staff/payroll-page")),
  "/staff/tips": lazy(() => import("@/features/staff/tips-page")),
  "/me/work": lazy(() => import("@/features/staff/my-work-page")),
  "/me/goals": lazy(() => import("@/features/goals/my-goals-page")),
  "/staff/goals": lazy(() => import("@/features/goals/goals-page")),
  "/settings/audit": lazy(() => import("@/features/audit/audit-page")),
  "/deliveries": lazy(() => import("@/features/delivery/deliveries-page")),
  "/courier": lazy(() => import("@/features/delivery/courier-page")),
  "/promotions": lazy(() => import("@/features/promotions/promotions-page")),
  "/banquets": lazy(() => import("@/features/banquets/banquets-page")),
  "/services": lazy(() => import("@/features/services/services-page")),
  "/sms": lazy(() => import("@/features/sms/sms-page")),
  "/fraud": lazy(() => import("@/features/fraud/fraud-page")),
  "/support": lazy(() => import("@/features/support/support-page")),
  "/settings/site": lazy(() => import("@/features/site/site-page")),
  "/customers": lazy(() => import("@/features/customers/customers-page")),
  "/debts": lazy(() => import("@/features/debts/debts-page")),
  "/suppliers": lazy(() => import("@/features/suppliers/suppliers-page")),
  "/cash-shifts": lazy(() => import("@/features/cash/cash-shifts-page")),
  "/reservations": lazy(() => import("@/features/reservations/reservations-page")),
  "/kitchen-stations": lazy(() => import("@/features/stations/stations-page")),
  "/pos": lazy(() => import("@/features/pos/pos-page")),
  "/kitchen": lazy(() => import("@/features/kds/kds-page")),
};

/** Подстраницы разделов: свой адрес, права и тариф; в меню подсвечивается раздел-родитель (`findNav`). */
const SUB_PAGES: { path: string; permission: NavItem["permission"]; feature: NavItem["feature"]; page: ReturnType<typeof lazy> }[] = [
  { path: "/purchases/new", permission: "stock_documents.create", feature: "core.inventory_basic", page: lazy(() => import("@/features/finance/purchase-page")) },
];

function SubPage({ sub }: { sub: (typeof SUB_PAGES)[number] }) {
  const Page = sub.page;
  return (
    <PageGuard permission={sub.permission} feature={sub.feature}>
      <Suspense fallback={<Skeleton className="h-96" />}>
        <Page />
      </Suspense>
    </PageGuard>
  );
}

function NavPage({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const Page = PAGES[item.path];
  return (
    <PageGuard permission={item.permission} anyPermission={item.anyPermission} feature={item.feature}>
      {Page ? (
        <Suspense fallback={<Skeleton className="h-96" />}>
          <Page />
        </Suspense>
      ) : (
        <ComingSoon title={t(item.label)} />
      )}
    </PageGuard>
  );
}

/** Касса/кухня: «Назад» браузера не уводит с сайта (`useBackGuard`). */
function BackGuard() {
  useBackGuard();
  return null;
}

/** «/» → первый доступный пользователю раздел (кассиру — его разделы, владельцу — дашборд). */
function HomeRedirect() {
  const sections = useVisibleNav();
  const launch = useVisibleLaunch();
  // Пока права не загружены, видны только пункты без прав («Мой кабинет») — ждём, чтобы не увести не туда
  const perms = usePermissions();
  const me = useMe();
  const can = useCan();
  const impersonating = useAuth((s) => !!s.impersonation);
  // Есть аналитика — дашборд; иначе рабочий экран (касса/кухня), иначе первый раздел
  const first = (sections[0]?.id === "analytics" ? sections[0].items[0]?.path : launch[0]?.path) ?? sections[0]?.items[0]?.path;
  if (!first || perms.isPending || me.isPending) return <Skeleton className="h-96" />;
  // Новая компания: мастер онбординга (ТЗ 16 §16.12) — для тех, кто настраивает заведение
  // Сеанс поддержки не уводит в мастер клиента: поддержка смотрит заведение как есть (в read-only мастер всё равно не сохранить)
  if (me.data && !me.data.company.onboardingCompleted && can("settings.manage") && !impersonating) return <Navigate to="/onboarding" replace />;
  return <Navigate to={first} replace />;
}

function AnonRedirect() {
  return <Navigate to={deviceEntry() ?? "/login"} replace />;
}

export const router = createBrowserRouter([
  { path: "/login", element: <GuestOnly><Outlet /></GuestOnly>, children: [{ index: true, lazy: route(() => import("@/features/auth/pages/login-page")) }] },
  { path: "/register", element: <GuestOnly><Outlet /></GuestOnly>, children: [{ index: true, lazy: route(() => import("@/features/auth/pages/register-page")) }] },
  { path: "/forgot-password", element: <GuestOnly><Outlet /></GuestOnly>, children: [{ index: true, lazy: route(() => import("@/features/auth/pages/forgot-password-page")) }] },
  { path: "/reset-password", element: <GuestOnly><Outlet /></GuestOnly>, children: [{ index: true, lazy: route(() => import("@/features/auth/pages/reset-password-page")) }] },
  { path: "/terminal/activate", lazy: route(() => import("@/features/terminal/activate-page")) },
  { path: "/terminal", element: <GuestOnly><Outlet /></GuestOnly>, children: [{ index: true, lazy: route(() => import("@/features/terminal/pin-page")) }] },
  // Экраны без PIN (кухня, готовность): открываются по токену устройства
  { path: "/screen", lazy: route(() => import("@/features/terminal/screen-page")) },
  {
    path: "/",
    element: <RequireAuth><AdminShell /></RequireAuth>,
    children: [
      { index: true, element: <HomeRedirect /> },
      // Презентация Coca AI (кнопка в шапке), доступна всем
      { path: "ai", lazy: route(() => import("@/features/ai/ai-page")) },
      ...NAV_ITEMS.filter((i) => !i.fullscreen).map((item) => ({ path: item.path.slice(1), element: <NavPage item={item} /> })),
      ...SUB_PAGES.map((sub) => ({ path: sub.path.slice(1), element: <SubPage sub={sub} /> })),
    ],
  },
  // Касса и KDS — на весь экран, без оболочки ERP; те же проверки прав и тарифа
  ...NAV_ITEMS.filter((i) => i.fullscreen).map((item) => ({
    path: item.path,
    element: <RequireAuth><BackGuard /><NavPage item={item} /></RequireAuth>,
  })),
  ...(import.meta.env.DEV ? [{ path: "/dev/ui-kit", lazy: route(() => import("@/dev/ui-kit-page")) }] : []),
  { path: "/onboarding", element: <RequireAuth><Suspense fallback={<Skeleton className="h-96" />}><OnboardingPage /></Suspense></RequireAuth> },
  { path: "/impersonate", lazy: route(() => import("@/features/billing/entry-pages").then((m) => ({ default: m.ImpersonatePage }))) },
  { path: "/invite", lazy: route(() => import("@/features/billing/entry-pages").then((m) => ({ default: m.InvitePage }))) },
  { path: "/logged-out", element: <AnonRedirect /> },
  { path: "*", element: <Navigate to="/" replace /> },
]);
